import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Info, Loader2, FileEdit, Save, CheckCircle, XCircle, TriangleAlert
} from 'lucide-react';

const InfoTab = ({ device }) => {
    const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
    const isAdmin = userInfo.levelRight >= 1;
    const canChangeComments = userInfo.canChangeComments === 1;

    const deviceName = device?.name || device?.deviceName || '';

    const [info, setInfo] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // ✅ Состояния для замечаний
    const [statusText, setStatusText] = useState('');
    const [originalText, setOriginalText] = useState('');
    const [saveStatus, setSaveStatus] = useState('idle');
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        if (deviceName) {
            loadInfo();
        }
    }, [deviceName]);

    // ✅ Инициализация замечаний при загрузке устройства
    useEffect(() => {
        const initialText = device?.statusDescription || '';
        setStatusText(initialText);
        setOriginalText(initialText);
    }, [device?.statusDescription]);

    const hasChanges = statusText !== originalText;

    const loadInfo = async () => {
        if (!deviceName) return;

        setIsLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('token');
            const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5022';

            const response = await axios.get(
                `${API_URL}/api/devices/karat307/${deviceName}/info`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            console.log('📋 Карат-307 Info:', response.data);
            setInfo(response.data);
        } catch (err) {
            console.error('Ошибка загрузки информации:', err);
            setError('Не удалось загрузить информацию');
        } finally {
            setIsLoading(false);
        }
    };

    // ✅ Обработчик сохранения замечаний
    const handleSaveStatus = async () => {
        if (!canChangeComments || !deviceName) return;

        setSaveStatus('saving');
        setErrorMessage('');

        try {
            const token = localStorage.getItem('token');
            const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5022';

            const response = await axios.put(
                `${API_URL}/api/devices/karat307/${deviceName}/status`,
                { statusDescription: statusText },
                { headers: { 'Authorization': `Bearer ${token}` } }
            );

            console.log('✅ Замечания сохранены:', response.data);
            setSaveStatus('success');
            setOriginalText(statusText);
            setTimeout(() => setSaveStatus('idle'), 2500);
        } catch (err) {
            console.error('❌ Ошибка сохранения замечаний:', err);
            setSaveStatus('error');
            setErrorMessage(
                err.response?.data?.message ||
                err.message ||
                'Не удалось сохранить замечания'
            );
        }
    };

    if (!deviceName) {
        return (
            <div className="bg-slate-800 rounded-xl p-12 border border-slate-700 text-center">
                <p className="text-slate-400">Устройство не выбрано</p>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="animate-spin h-8 w-8 text-green-500 mx-auto mb-3" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-500/20 border border-red-500/50 text-red-200 px-4 py-3 rounded-lg text-sm">
                {error}
            </div>
        );
    }

    if (!info) {
        return (
            <div className="bg-slate-800 rounded-xl p-12 border border-slate-700 text-center">
                <p className="text-slate-400">Нет данных</p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Основная информация */}
            <div className="bg-slate-900 rounded-xl border border-slate-700 overflow-hidden">
                <div className="bg-slate-800 px-4 py-2 border-b border-slate-700 flex items-center gap-2">
                    <Info className="w-5 h-5 text-green-400" />
                    <h4 className="text-white font-semibold text-sm">Информация об устройстве</h4>
                </div>
                <div className="divide-y divide-slate-700/50">
                    <InfoRow label="Данные от" value={info.datetime} />
                    <InfoRow label="Время в приборе" value={info.deviceTime} />
                    <InfoRow label="Модификация" value={info.modification} />
                    <InfoRow label="Серийный номер" value={info.serialNumber} />
                    <InfoRow label="Версия платы вычислителя" value={info.hardVersion} />
                    <InfoRow label="Версия платы коммутации" value={info.crossVersion} />
                    <InfoRow label="Уровень заряда батареи" value={info.powerBattery} />
                    <InfoRow label="Время последнего изменения калибровочных констант" value={info.lastEditTime} />
                    <InfoRow label="Контрольная сумма конфигурации" value={info.configChecksum} />
                    <InfoRow label="Количество каналов расхода" value={info.numVChannel} />
                    <InfoRow label="Количество каналов температуры" value={info.numRChannel} />
                    <InfoRow label="Количество каналов давления" value={info.numIChannel} />
                    <InfoRow label="Номер телефона" value={info.telNumber} />
                </div>
            </div>

            {/* ✅ Замечания — с рабочей кнопкой сохранения */}
            <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
                <h4 className="text-white font-semibold mb-3 flex items-center gap-2 text-sm">
                    <FileEdit className="w-5 h-5 text-slate-300" />
                    Замечания
                </h4>
                <div className="space-y-3">
                    {/* ✅ Предупреждение */}
                    {!canChangeComments && (   // ✅ было !isAdmin
                        <div className="bg-yellow-500/10 border border-yellow-500/30 text-yellow-200 px-3 py-2 rounded-lg text-xs flex items-center gap-2">
                            <TriangleAlert className="w-4 h-4 text-yellow-400 flex-shrink-0" />
                            <span>Для изменения замечаний требуются соответствующие права</span>
                        </div>
                    )}

                    <div>
                        <label className="block text-slate-400 text-xs mb-1 flex items-center gap-2">
                            <span>Статус устройства</span>
                            {hasChanges && (
                                <span className="text-yellow-400 text-xs flex items-center gap-1">
                                    <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 animate-pulse"></span>
                                    не сохранено
                                </span>
                            )}
                        </label>
                        <textarea
                            value={statusText}
                            onChange={(e) => setStatusText(e.target.value)}
                            className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm min-h-[80px] focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed resize-y"
                            placeholder="Введите замечания..."
                            readOnly={!canChangeComments}       // ✅ было !isAdmin
                            disabled={saveStatus === 'saving'}
                        />
                    </div>

                    {/* ✅ Сообщение об успехе */}
                    {saveStatus === 'success' && (
                        <div className="flex items-center gap-2 px-3 py-2 bg-green-500/10 border border-green-500/30 rounded-lg text-green-300 text-sm">
                            <CheckCircle className="w-4 h-4 flex-shrink-0" />
                            <span>Замечания успешно сохранены</span>
                        </div>
                    )}

                    {/* ✅ Сообщение об ошибке */}
                    {saveStatus === 'error' && (
                        <div className="flex items-center gap-2 px-3 py-2 bg-red-500/10 border border-red-500/30 rounded-lg text-red-300 text-sm">
                            <XCircle className="w-4 h-4 flex-shrink-0" />
                            <span>{errorMessage}</span>
                        </div>
                    )}

                    {/* ✅ Кнопка сохранения */}
                    {canChangeComments && (   // ✅ было isAdmin
                        <button
                            onClick={handleSaveStatus}
                            disabled={saveStatus === 'saving' || !hasChanges}
                            className="px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg text-sm font-medium transition flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {saveStatus === 'saving' ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Сохранение...
                                </>
                            ) : (
                                <>
                                    <Save className="w-4 h-4" />
                                    Сохранить замечания
                                </>
                            )}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

const InfoRow = ({ label, value, icon: Icon }) => (
    <div className="flex items-center px-4 py-2 hover:bg-slate-700/20 transition">
        {Icon && <Icon className="w-4 h-4 text-slate-500 mr-2 flex-shrink-0" />}
        <div className="text-slate-300 text-sm flex-1">{label}</div>
        <div className="text-white text-sm font-medium min-w-[150px] text-right">{value || '—'}</div>
    </div>
);

export default InfoTab;